/* SEIFE scratch-to-reveal welcome popup.
   Vanilla JS, no dependencies. Everything lives under the SeifeScratchPopup
   namespace; all DOM hooks are data-sfp-* attributes owned by
   sections/scratch-popup.liquid. The Klaviyo embedded form (mounted into
   [data-sfp-klaviyo-mount]) owns email submission, consent, unique-coupon
   display and the welcome flow; this file only hosts it and listens for its
   public "klaviyoForms" events to advance the shell. */
(function () {
  'use strict';

  if (window.SeifeScratchPopup) return; // theme-editor section reloads re-inject this script

  var STORE_KEY = 'seife_scratch_popup_v1';
  var SESSION_KEY = 'seife_scratch_popup_v1_shown';
  var DAY = 86400000;

  /* localStorage can throw (Safari private mode, storage disabled) — the
     popup must degrade to session-only frequency capping, never crash. */
  function readState() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || null; } catch (e) { return null; }
  }
  function writeState(state) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify({ s: state, t: Date.now() })); } catch (e) { /* no-op */ }
  }
  function sessionShown(set) {
    try {
      if (set) { sessionStorage.setItem(SESSION_KEY, '1'); return true; }
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) { return false; }
  }

  var api = {
    root: null,
    cfg: null,
    scratch: null,
    _opened: false,
    _armed: false,
    _lockOffset: 0,
    _opener: null,
    _retryTimer: null,

    init: function (root) {
      if (!root || root.dataset.sfpInit === '1') return;
      root.dataset.sfpInit = '1';
      this.root = root;

      var d = root.dataset;
      this.cfg = {
        enabled: d.enabled === 'true',
        formId: d.formId || '',
        companyId: d.companyId || '',
        locale: d.locale || 'en',
        delay: (parseInt(d.delay, 10) || 6) * 1000,
        scrollPct: (parseInt(d.scrollPct, 10) || 30) / 100,
        threshold: (parseInt(d.threshold, 10) || 40) / 100,
        closeDays: parseInt(d.closeDays, 10) || 7,
        announce: d.announce || '',
        debug: d.debug === 'true',
        designMode: d.designMode === 'true',
        reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches
      };

      this._bindShell();
      if (this.cfg.debug) this._bindDebug();

      /* Public arming requires a real Klaviyo form — the shell must not
         collect attention it cannot pay off with a code. Editor preview and
         debug mode bypass arming (manual open only / section select). */
      if (this.cfg.designMode) return;
      if (!this.cfg.enabled) return;
      if (!this.cfg.formId && !this.cfg.debug) return;

      this._arm();
    },

    /* ── Display triggers: first of (delay timer | scroll depth) ── */
    _arm: function () {
      if (this._armed || sessionShown() || !this._suppressionClear()) return;
      this._armed = true;
      var self = this;
      var fired = false;
      function fire() {
        if (fired) return;
        fired = true;
        window.removeEventListener('scroll', onScroll);
        self._tryOpen(0);
      }
      var timer = setTimeout(fire, this.cfg.delay);
      function onScroll() {
        var doc = document.documentElement;
        var max = doc.scrollHeight - window.innerHeight;
        if (max > 0 && window.scrollY / max >= self.cfg.scrollPct) {
          clearTimeout(timer);
          fire();
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
    },

    _suppressionClear: function () {
      var st = readState();
      if (!st || !st.t) return true;
      var age = Date.now() - st.t;
      if (st.s === 'done') return age > 365 * DAY;
      if (st.s === 'revealed') return age > DAY;
      if (st.s === 'closed') return age > this.cfg.closeDays * DAY;
      return true;
    },

    /* A trigger fired but the page may not be ready to take a modal:
       cart drawer open, another dialog up, or the cookie-consent banner
       showing. Re-check on an interval instead of stealing the screen. */
    _blocked: function () {
      if (document.body.classList.contains('cart-drawer-scroll-locked')) return true;
      var dialogs = document.querySelectorAll('[aria-modal="true"]');
      for (var i = 0; i < dialogs.length; i++) {
        if (!this.root.contains(dialogs[i]) && dialogs[i].offsetParent !== null) return true;
      }
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner()) return true;
      } catch (e) { /* treat privacy API errors as not-blocking */ }
      var banner = document.querySelector('#shopify-pc__banner');
      if (banner && banner.offsetParent !== null) return true;
      return false;
    },

    _tryOpen: function (attempt) {
      if (this._opened || sessionShown()) return;
      if (this._blocked()) {
        if (attempt < 40) {
          var self = this;
          this._retryTimer = setTimeout(function () { self._tryOpen(attempt + 1); }, 2500);
        }
        return;
      }
      this.open();
    },

    /* ── Open / close ── */
    open: function (forced) {
      if (this._opened) return;
      this._opened = true;
      if (!forced) sessionShown(true);

      var img = this.root.querySelector('.sfp__box-img');
      if (img && !img.complete) img.loading = 'eager';

      this._opener = document.activeElement;
      this._lockBody();
      this.root.hidden = false;
      this.setStage('scratch', true);
      this._mountKlaviyo();

      /* Everything the popup needs must NOT wait on requestAnimationFrame —
         rAF stalls in backgrounded tabs, which would leave an unhidden but
         invisible, uninitialized dialog. Init synchronously; only the
         transition class waits, via a short timeout after a forced reflow. */
      this._initScratch();
      var close = this.root.querySelector('.sfp__close');
      if (close) close.focus();
      void this.root.offsetWidth;
      var self = this;
      setTimeout(function () { self.root.classList.add('is-open'); }, 20);
    },

    close: function () {
      if (!this._opened) return;
      this._opened = false;
      clearTimeout(this._retryTimer);

      var stage = this.root.getAttribute('data-stage');
      var st = readState();
      var done = st && st.s === 'done';
      if (!done && !this.cfg.designMode) {
        /* Revealed-but-not-submitted gets a short 24h cooldown; a plain
           close gets the configured 7-day suppression. */
        writeState(stage === 'offer' || stage === 'email' ? 'revealed' : 'closed');
      }

      this.root.classList.remove('is-open');
      var self = this;
      var finish = function () {
        self.root.hidden = true;
        self._unlockBody();
        if (self._opener && document.contains(self._opener)) self._opener.focus();
      };
      if (this.cfg.reduced) finish();
      else setTimeout(finish, 300);
    },

    /* ── Stages: scratch → offer → email → success ── */
    setStage: function (stage, skipFocus) {
      this.root.setAttribute('data-stage', stage);
      this._debugInfo();
      if (skipFocus) return;
      var target =
        stage === 'offer' ? this.root.querySelector('[data-sfp-claim]') :
        stage === 'email' ? (this.root.querySelector('.sfp__klaviyo input[type="email"]') || this.root.querySelector('[data-sfp-panel~="email"] .sfp__headline')) :
        stage === 'success' ? this.root.querySelector('[data-sfp-shop]') :
        this.root.querySelector('[data-sfp-focus="scratch"]');
      if (target) {
        if (!target.hasAttribute('tabindex') && !/^(a|button|input)$/i.test(target.tagName)) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    },

    reveal: function () {
      if (this.root.getAttribute('data-stage') !== 'scratch') return;
      var live = this.root.querySelector('[data-sfp-live]');
      if (live) live.textContent = this.cfg.announce;
      var self = this;
      setTimeout(function () { self.setStage('offer'); }, this.cfg.reduced ? 0 : 550);
    },

    /* ── Body scroll lock: vars first, class second, one paint ── */
    _lockBody: function () {
      if (document.body.classList.contains('sfp-locked') ||
          document.body.classList.contains('cart-drawer-scroll-locked')) return;
      this._lockOffset = window.scrollY;
      var gap = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.setProperty('--sfp-lock-offset', this._lockOffset + 'px');
      document.body.style.setProperty('--sfp-lock-gap', gap + 'px');
      document.body.classList.add('sfp-locked');
    },
    _unlockBody: function () {
      if (!document.body.classList.contains('sfp-locked')) return;
      document.body.classList.remove('sfp-locked');
      document.body.style.removeProperty('--sfp-lock-offset');
      document.body.style.removeProperty('--sfp-lock-gap');
      window.scrollTo({ top: this._lockOffset, left: 0, behavior: 'instant' });
    },

    /* ── Shell events ── */
    _bindShell: function () {
      var self = this;
      this.root.addEventListener('click', function (e) {
        if (e.target.closest('[data-sfp-close]')) { self.close(); return; }
        if (e.target.closest('[data-sfp-reveal]')) { if (self.scratch) self.scratch.complete(); else self.reveal(); return; }
        if (e.target.closest('[data-sfp-claim]')) { self.setStage('email'); return; }
      });

      this.root.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.stopPropagation(); self.close(); return; }
        if (e.key !== 'Tab') return;
        /* Focus trap over the dialog's currently visible focusables. */
        var sel = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
        var list = Array.prototype.filter.call(self.root.querySelectorAll(sel), function (el) {
          return el.offsetParent !== null;
        });
        if (!list.length) return;
        var first = list[0], last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      /* Klaviyo's embedded form announces lifecycle on window. A submit of
         OUR form completes the funnel: long suppression + success stage
         (the embed itself keeps showing Klaviyo's success step with the
         unique coupon — we only swap the shell copy around it). */
      window.addEventListener('klaviyoForms', function (e) {
        if (!e.detail || !self.cfg.formId) return;
        if (String(e.detail.formId) !== String(self.cfg.formId)) return;
        if (e.detail.type === 'submit' || e.detail.type === 'stepSubmit') {
          writeState('done');
          if (e.detail.type === 'submit') self.setStage('success');
        }
      });

      /* Theme editor: preview when the merchant selects the section. */
      if (this.cfg.designMode) {
        document.addEventListener('shopify:section:select', function (e) {
          if (e.detail && e.detail.sectionId && self.root.dataset.sectionId === e.detail.sectionId) self.open(true);
        });
        document.addEventListener('shopify:section:deselect', function (e) {
          if (e.detail && e.detail.sectionId && self.root.dataset.sectionId === e.detail.sectionId) self.close();
        });
      }
    },

    /* ── Klaviyo bootstrap: load klaviyo.js once, only if truly absent ── */
    _mountKlaviyo: function () {
      if (!this.cfg.formId) return;
      var present = !!document.querySelector('script[src*="klaviyo.js"], script[src*="static.klaviyo.com/onsite"]');
      if (!present && this.cfg.companyId) {
        var s = document.createElement('script');
        s.async = true;
        s.src = 'https://static.klaviyo.com/onsite/js/' + encodeURIComponent(this.cfg.companyId) + '/klaviyo.js';
        document.head.appendChild(s);
      }
    },

    /* ── Scratch engine ── */
    _initScratch: function () {
      var zone = this.root.querySelector('[data-sfp-zone]');
      var canvas = this.root.querySelector('[data-sfp-canvas]');
      if (!zone || !canvas || this.scratch) return;

      var ctx = canvas.getContext && canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        /* No canvas support: show the prize immediately; the "reveal
           without scratching" button already covers interaction. */
        zone.classList.add('is-done');
        canvas.remove();
        return;
      }

      var self = this;
      var cfgThreshold = this.cfg.threshold;
      var reduced = this.cfg.reduced;
      var label = canvas.dataset.scratchLabel || 'SCRATCH HERE';
      var done = false, started = false, drawing = false;
      var last = null, moveCount = 0, lastCheck = 0, pct = 0, baseline = 0;

      /* The scratch layer IS the box: the canvas draws the box's crop from
         the uploaded render (CORS-safe on Shopify's CDN, so alpha
         measurement keeps working) and erasing it exposes the offer text on
         the popup background behind. Until the source loads — or if there
         is no image — the canvas falls back to the original charcoal
         coating over the kraft stand-in. */
      var boxwrap = this.root.querySelector('[data-sfp-boxwrap]');
      var artCrop = null;

      /* Auto-fit to the artwork. A PNG box render is usually centred inside
         transparent padding; that padding is what made the box render small
         with dead space around it. Measuring the opaque bounding box gives
         us both the source crop to draw AND the wrapper's true aspect, so
         the wrapper hugs the box no matter which asset is uploaded — no
         manual slider calibration to go stale when the render is swapped.
         Falls back silently to the manual box-area settings if the image is
         edge-to-edge opaque or unreadable (cross-origin). */
      function fitToArtwork() {
        try {
          var nw = boxImg.naturalWidth, nh = boxImg.naturalHeight;
          if (!nw || !nh) return;
          var s = document.createElement('canvas');
          var k = Math.min(1, 320 / Math.max(nw, nh)); /* sample small: fast */
          s.width = Math.max(1, Math.round(nw * k));
          s.height = Math.max(1, Math.round(nh * k));
          var sc = s.getContext('2d');
          sc.drawImage(boxImg, 0, 0, s.width, s.height);
          var d = sc.getImageData(0, 0, s.width, s.height).data;
          var minX = s.width, maxX = -1, minY = s.height, maxY = -1;
          for (var y = 0; y < s.height; y++) {
            for (var x = 0; x < s.width; x++) {
              if (d[(y * s.width + x) * 4 + 3] >= 128) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX < 0) return; /* fully transparent — keep manual settings */
          var cw = (maxX - minX + 1) / s.width;
          var ch = (maxY - minY + 1) / s.height;
          if (cw > 0.985 && ch > 0.985) return; /* no padding to trim */
          artCrop = { x: minX / s.width, y: minY / s.height, w: cw, h: ch };
          /* the zone now spans the whole wrapper, and the wrapper takes the
             artwork's own proportions */
          boxwrap.style.setProperty('--sfp-zx', '0%');
          boxwrap.style.setProperty('--sfp-zy', '0%');
          boxwrap.style.setProperty('--sfp-zw', '100%');
          boxwrap.style.setProperty('--sfp-zh', '100%');
          boxwrap.style.setProperty('--sfp-box-aspect',
            ((cw * nw) / (ch * nh)).toFixed(4));
        } catch (e) { /* cross-origin: keep manual settings */ }
      }

      function sourceRect() {
        if (artCrop) return artCrop;
        var cs = getComputedStyle(boxwrap);
        return {
          x: (parseFloat(cs.getPropertyValue('--sfp-zx')) || 0) / 100,
          y: (parseFloat(cs.getPropertyValue('--sfp-zy')) || 0) / 100,
          w: (parseFloat(cs.getPropertyValue('--sfp-zw')) || 100) / 100,
          h: (parseFloat(cs.getPropertyValue('--sfp-zh')) || 100) / 100
        };
      }

      var boxImg = null, boxReady = false;
      if (canvas.dataset.boxSrc) {
        boxImg = new Image();
        boxImg.crossOrigin = 'anonymous';
        boxImg.onload = function () {
          boxReady = true;
          fitToArtwork();
          if (!started && !done) paint();
        };
        boxImg.src = canvas.dataset.boxSrc;
      }

      function paint() {
        var r = zone.getBoundingClientRect();
        if (r.width < 10) return;
        var dpr = Math.min(window.devicePixelRatio || 1, 3);
        var old = null;
        if (started && canvas.width > 0) {
          old = document.createElement('canvas');
          old.width = canvas.width; old.height = canvas.height;
          old.getContext('2d').drawImage(canvas, 0, 0);
        }
        canvas.width = Math.round(r.width * dpr);
        canvas.height = Math.round(r.height * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (boxReady) {
          /* Draw the box itself as the scratchable surface: the artwork's
             opaque region maps onto the whole canvas. */
          var cr = sourceRect();
          ctx.drawImage(
            boxImg,
            cr.x * boxImg.naturalWidth, cr.y * boxImg.naturalHeight,
            cr.w * boxImg.naturalWidth, cr.h * boxImg.naturalHeight,
            0, 0, r.width, r.height
          );
        } else {
          /* Fallback (image absent or still loading): the original flat
             matte charcoal coating with a whisper of grain. */
          ctx.fillStyle = '#131313';
          ctx.fillRect(0, 0, r.width, r.height);
          var tile = document.createElement('canvas');
          tile.width = tile.height = 128;
          var tctx = tile.getContext('2d');
          var grain = tctx.createImageData(128, 128);
          for (var gi = 0; gi < grain.data.length; gi += 4) {
            var lum = Math.random() > 0.5 ? 255 : 0;
            grain.data[gi] = grain.data[gi + 1] = grain.data[gi + 2] = lum;
            grain.data[gi + 3] = (Math.random() * 8) | 0; /* 0–3% alpha */
          }
          tctx.putImageData(grain, 0, 0);
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.fillStyle = ctx.createPattern(tile, 'repeat');
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.restore();
        }

        /* SCRATCH HERE, centred over the box. A soft dark halo keeps the
           white label readable on light kraft without a printed panel. */
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.55)';
        ctx.shadowBlur = 10;
        ctx.fillStyle = 'rgba(255,255,255,0.96)';
        var fs = Math.max(13, Math.min(20, r.width * 0.09));
        try { ctx.letterSpacing = '0.14em'; } catch (e) { /* older engines */ }
        ctx.font = '700 ' + fs + 'px ' + (getComputedStyle(document.body).fontFamily || 'sans-serif');
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, r.width / 2, r.height / 2);
        ctx.restore();

        if (old) {
          /* Preserve scratched-away areas across resize/orientation. */
          ctx.save();
          ctx.globalCompositeOperation = 'destination-out';
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          var mask = document.createElement('canvas');
          mask.width = old.width; mask.height = old.height;
          var mctx = mask.getContext('2d');
          mctx.fillStyle = '#000';
          mctx.fillRect(0, 0, mask.width, mask.height);
          mctx.globalCompositeOperation = 'destination-out';
          mctx.drawImage(old, 0, 0);
          ctx.drawImage(mask, 0, 0, mask.width, mask.height, 0, 0, canvas.width, canvas.height);
          ctx.restore();
        }
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineCap = ctx.lineJoin = 'round';

        /* Re-baseline whenever we repaint an untouched coating (initial
           paint, and any resize before the first stroke). */
        if (!started) baseline = rawClear();
      }

      function pos(e) {
        var r = canvas.getBoundingClientRect();
        return { x: e.clientX - r.left, y: e.clientY - r.top };
      }

      function brush(p, q) {
        var w = Math.max(30, canvas.getBoundingClientRect().width * 0.15);
        ctx.lineWidth = w;
        ctx.beginPath();
        if (q) { ctx.moveTo(q.x, q.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
        ctx.beginPath();
        ctx.arc(p.x, p.y, w / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      function rawClear() {
        /* Alpha sampling on a coarse stride — ~2k samples regardless of
           canvas size, throttled by the caller. */
        var w = canvas.width, h = canvas.height;
        if (!w || !h) return 0;
        var data;
        try { data = ctx.getImageData(0, 0, w, h).data; } catch (e) { return 0; }
        var stride = Math.max(1, Math.floor(Math.sqrt((w * h) / 2000)));
        var clear = 0, total = 0;
        for (var y = 0; y < h; y += stride) {
          for (var x = 0; x < w; x += stride) {
            total++;
            if (data[(y * w + x) * 4 + 3] < 128) clear++;
          }
        }
        return total ? clear / total : 0;
      }

      /* A PNG box render is not a solid rectangle — its own transparent
         pixels (the perspective taper at top and bottom) read as "already
         scratched". Measuring progress against the freshly-painted state
         keeps the threshold meaning "40% of the actual box removed". */
      function measure() {
        if (baseline >= 0.999) return 0;
        return Math.min(1, Math.max(0, (rawClear() - baseline) / (1 - baseline)));
      }

      function maybeComplete(force) {
        var now = Date.now();
        if (!force && now - lastCheck < 160) return;
        lastCheck = now;
        pct = measure();
        self._debugInfo(pct);
        if (pct >= cfgThreshold) complete();
      }

      function complete() {
        if (done) return;
        done = true;
        drawing = false;
        zone.classList.add('is-done');
        if (reduced) {
          canvas.style.opacity = '0';
          self.reveal();
        } else {
          canvas.style.transition = 'opacity 0.5s ease';
          canvas.style.opacity = '0';
          self.reveal();
        }
      }

      canvas.addEventListener('pointerdown', function (e) {
        if (done) return;
        e.preventDefault();
        try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* synthetic or stale pointer ids */ }
        drawing = true;
        if (!started) { started = true; zone.classList.add('is-started'); }
        last = pos(e);
        brush(last, null);
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!drawing || done) return;
        e.preventDefault();
        var p = pos(e);
        brush(p, last);
        last = p;
        if (++moveCount % 4 === 0) maybeComplete(false);
      });
      function stop() { if (drawing) { drawing = false; maybeComplete(true); } }
      canvas.addEventListener('pointerup', stop);
      canvas.addEventListener('pointercancel', stop);

      /* Non-pointer path: the canvas is a focusable button (role/tabindex
         set in Liquid); Enter or Space reveals the offer outright. */
      canvas.addEventListener('keydown', function (e) {
        if (done) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          complete();
        }
      });

      if ('ResizeObserver' in window) {
        var ro = new ResizeObserver(function () { if (!done) paint(); });
        ro.observe(zone);
      } else {
        window.addEventListener('resize', function () { if (!done) paint(); });
      }
      paint();

      this.scratch = { complete: complete, measure: measure, get pct() { return pct; } };
    },

    /* ── Debug strip (only rendered when the theme setting is on) ── */
    _bindDebug: function () {
      var self = this;
      var bar = this.root.querySelector('[data-sfp-debug]');
      if (!bar) return;
      bar.addEventListener('click', function (e) {
        var b = e.target.closest('button');
        if (!b) return;
        if (b.hasAttribute('data-sfp-debug-open')) { self._opened = false; self.root.hidden = true; self.open(true); }
        if (b.hasAttribute('data-sfp-debug-reset')) {
          try { localStorage.removeItem(STORE_KEY); sessionStorage.removeItem(SESSION_KEY); } catch (err) { /* no-op */ }
          self._debugInfo();
        }
        var stage = b.getAttribute('data-sfp-debug-stage');
        if (stage) self.setStage(stage);
      });
      this._debugInfo();
    },
    _debugInfo: function (pct) {
      if (!this.cfg || !this.cfg.debug) return;
      var el = this.root.querySelector('[data-sfp-debug-info]');
      if (!el) return;
      var st = readState();
      el.textContent =
        'stage=' + (this.root.getAttribute('data-stage') || '-') +
        ' pct=' + (pct != null ? Math.round(pct * 100) + '%' : '-') +
        ' locale=' + this.cfg.locale +
        ' form=' + (this.cfg.formId || 'NONE') +
        ' stored=' + (st ? st.s : 'none');
    }
  };

  window.SeifeScratchPopup = api;

  function boot() {
    var root = document.getElementById('seife-scratch-popup');
    if (root) api.init(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor replaces section DOM wholesale. */
  document.addEventListener('shopify:section:load', function () {
    var root = document.getElementById('seife-scratch-popup');
    if (root && root.dataset.sfpInit !== '1') {
      api._opened = false;
      api.scratch = null;
      api.init(root);
    }
  });
  document.addEventListener('shopify:section:unload', function () {
    if (document.body.classList.contains('sfp-locked')) {
      document.body.classList.remove('sfp-locked');
      document.body.style.removeProperty('--sfp-lock-offset');
      document.body.style.removeProperty('--sfp-lock-gap');
    }
  });
})();
